import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Info } from '@/components/icons/barrel';
import { AccountOption } from '../utils/vest-capital-table.types';
import { cn } from '@/utils/cn';
import { tableRows } from './vest-capital-table';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui';
import DottedTexture from '@/assets/branding/dotted-texture.svg';

interface MobileAccountCardProps {
  option: AccountOption;
  onButtonClick: (priceId: string) => void;
  activePlanPriceId?: string | null;
  isEval: boolean;
  isMissingEmail: boolean;
  hasEval: boolean;
  isLoading: boolean;
  index: number;
}

const tags = ['Easy Challenge', 'Normal Challenge', 'Best Rewards'];
const classes = ['', '', 'border-primary text-primary'];

export function MobileAccountCard({
  option,
  onButtonClick,
  activePlanPriceId = null,
  isEval,
  isMissingEmail,
  hasEval,
  isLoading,
  index,
}: MobileAccountCardProps) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isActive = hasEval && activePlanPriceId === option.priceId;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="!rounded-t-3xl border-none sm:!rounded-3xl">
          <div className="relative flex flex-col gap-8 overflow-hidden !rounded-3xl p-5 pt-16">
            <img
              src={DottedTexture}
              className="pointer-events-none absolute top-0 z-[-1] h-auto w-full scale-[2]"
              alt="Dots"
            />

            <div className="flex flex-col items-start">
              <div className="flex w-full items-center justify-between gap-4">
                <div className="text-xl capitalize">{option.title}</div>

                <div
                  className={cn(
                    'rounded-md border border-border bg-[hsl(var(--primary-light))] px-3 py-2 capitalize',
                    classes[index],
                  )}
                >
                  {tags[index % tags.length]}
                </div>
              </div>

              <div className="mt-3 text-4xl font-medium">{option.size}</div>
              <div className="mt-1 capitalize text-vestgrey-500">
                Trading Capital
              </div>
            </div>

            <div className="flex flex-col gap-3">
              {tableRows.map((row) => (
                <div
                  key={row.key}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-2 text-xl capitalize text-vestgrey-500">
                    {t(row.labelKey)}

                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger>
                          <Info
                            size={14}
                            className="cursor-pointer text-vestgrey-500 hover:text-vestgrey-900"
                          />
                        </TooltipTrigger>
                        <TooltipContent className="normal-case">
                          {t(row.tooltipKey)}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  <div className="text-right text-xl">{option[row.key]}</div>
                </div>
              ))}
            </div>

            <Button
              variant={isActive ? 'yellow' : 'contrast'}
              size="xl"
              className="mt-7 w-full capitalize"
              disabled={isLoading || isMissingEmail || hasEval}
              onClick={() => onButtonClick(option.priceId)}
            >
              {isActive
                ? t('vestCapital.currentPlan')
                : t('vestCapital.startNow')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div
        className={cn(
          'flex cursor-pointer items-center justify-between rounded-2xl p-5',
          isActive ? 'glass-yellow' : 'glass',
        )}
        onClick={() => setIsOpen(true)}
      >
        <div className="flex flex-col items-start">
          <div className="flex items-center gap-4">
            <div className="text-xl capitalize">{option.title}</div>

            <div
              className={cn(
                'rounded-md border border-border bg-[hsl(var(--primary-light))] px-3 py-2 capitalize',
                classes[index],
              )}
            >
              {tags[index % tags.length]}
            </div>
          </div>

          <div className="mt-3 text-4xl font-medium">{option.size}</div>
          <div className="mt-1 capitalize text-vestgrey-500">
            Trading Capital
          </div>
        </div>

        <ArrowRight size={32} className="text-border" />
      </div>
    </>
  );
}
